import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, Check, Loader, Instagram } from 'lucide-react';

export const EnhancedUserDropdown = ({ 
  options, 
  value, 
  onChange, 
  placeholder = "Select a user profile",
  onInstagramLookup = null,
  isLookingUp = false,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [lastSearched, setLastSearched] = useState(''); // Track last searched term
  const [searchCache, setSearchCache] = useState(new Set()); // Cache searched terms
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const filteredOptions = options.filter(option => 
    (option.label || option.username || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Manual search trigger function (called on Enter key or explicit search)
  const triggerInstagramSearch = (term) => {
    if (!onInstagramLookup || isLookingUp) return;
    
    const cleanTerm = term.replace('@', '').trim();
    
    // Only search if we haven't searched this term before and it's valid
    if (cleanTerm.length >= 1 && !searchCache.has(cleanTerm.toLowerCase())) {
      console.log(`🔍 Manual search triggered for: ${cleanTerm}`);
      setLastSearched(cleanTerm);
      setSearchCache(prev => new Set([...prev, cleanTerm.toLowerCase()]));
      onInstagramLookup(cleanTerm);
    } else if (searchCache.has(cleanTerm.toLowerCase())) {
      console.log(`⚡ Already searched for: ${cleanTerm} - showing cached results`);
    }
  };

  // Clear cache when dropdown opens fresh
  useEffect(() => {
    if (isOpen && searchTerm === '') {
      setSearchCache(new Set());
      setLastSearched('');
    }
  }, [isOpen, searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        } else if (searchTerm.length >= 1) {
          // Trigger Instagram search when Enter is pressed with search term
          triggerInstagramSearch(searchTerm);
        }
        break;
      default:
        break;
    }
  };

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const getDisplayValue = () => {
    if (!value) return placeholder;
    return value.label || value.username || placeholder;
  };

  const isSelected = (option) => {
    return value && value.id === option.id;
  };

  const renderUserOption = (option, index) => {
    const isInstagramUser = option.source === 'instagram_lookup' || option.source === 'apify_scraper';
    
    return (
      <div
        key={option.id}
        className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
          highlightedIndex === index ? 'bg-purple-50' : 'hover:bg-gray-50'
        } ${isSelected(option) ? 'bg-purple-100' : ''}`}
        onClick={() => handleSelect(option)}
        onMouseEnter={() => setHighlightedIndex(index)}
      >
        {/* Avatar with Profile Picture Support */}
        <div className="flex-shrink-0 relative">
          {option.profilePicture ? (
            <img 
              src={option.profilePicture} 
              alt={option.displayName || option.username}
              className="w-10 h-10 rounded-full object-cover border-2 border-gray-200"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm ${
              isInstagramUser ? 'bg-gradient-to-r from-purple-500 to-pink-500' : 'bg-gray-400'
            } ${option.profilePicture ? 'hidden' : 'flex'}`}
          >
            {option.avatar || option.username?.charAt(1)?.toUpperCase() || 'U'}
          </div>

          {/* Instagram badge */}
          {isInstagramUser && (
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
              <Instagram className="text-white" size={10} />
            </div>
          )}

          {/* Verification badge */}
          {option.verified && (
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
              <Check className="text-white" size={10} />
            </div>
          )}
        </div>
        
        {/* User Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-gray-900 truncate">
              {option.displayName || option.username}
            </span>
            {option.verified && (
              <Check className="text-blue-500" size={14} />
            )}
            {isInstagramUser && (
              <Instagram className="text-pink-500" size={14} />
            )}
          </div>
          
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <span className="font-medium">{option.username}</span>
            {option.followers && (
              <>
                <span>•</span>
                <span className="font-semibold text-gray-700">
                  {typeof option.followers === 'number' ? 
                    option.followers.toLocaleString() : 
                    option.followers
                  } followers
                </span>
              </>
            )}
          </div>

          {/* Bio preview for Instagram users */}
          {option.bio && (
            <p className="text-xs text-gray-500 mt-1 truncate">{option.bio}</p>
          )}

          {/* Data source indicator */}
          {isInstagramUser && option.method && (
            <div className="flex items-center gap-1 mt-1">
              <div className="w-1.5 h-1.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"></div>
              <span className="text-xs text-gray-400">
                {option.method.includes('backend') ? 'Live Instagram Data' : 'Demo Data'}
              </span>
            </div>
          )}
        </div>
        
        {/* Selection indicator */}
        {isSelected(option) && (
          <Check className="text-purple-600" size={16} />
        )}
      </div>
    );
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className="flex items-center justify-between w-full px-4 py-3 border border-gray-300 rounded-lg bg-white cursor-pointer hover:border-gray-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {value && (
            <div className="w-6 h-6 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white font-semibold text-xs">
              {value.avatar || value.username?.charAt(1)?.toUpperCase() || 'U'}
            </div>
          )}
          <span className={`truncate ${!value ? 'text-gray-500' : 'text-gray-900'}`}>
            {getDisplayValue()}
          </span>
        </div>
        <ChevronDown 
          className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          size={20} 
        />
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                ref={inputRef}
                type="text"
                placeholder="Type @username and press Enter to search Instagram..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full pl-10 pr-24 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
              />
              {/* Manual Search Button */}
              {searchTerm.length >= 1 && !isLookingUp && (
                <button
                  onClick={() => triggerInstagramSearch(searchTerm)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 px-2 py-1 bg-purple-500 text-white text-xs rounded hover:bg-purple-600 transition-colors"
                >
                  Search
                </button>
              )}
              {isLookingUp && (
                <Loader className="absolute right-3 top-1/2 transform -translate-y-1/2 text-purple-500 animate-spin" size={16} />
              )}
            </div>
            {searchTerm.length >= 1 && !filteredOptions.length && !isLookingUp && (
              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                <div className="flex items-center gap-2">
                  <Instagram size={14} />
                  <span>Press <kbd className="px-1 py-0.5 bg-white border rounded text-xs">Enter</kbd> or click <strong>Search</strong> to lookup "@{searchTerm.replace('@', '')}" on Instagram</span>
                </div>
              </div>
            )}
          </div>

          {/* Options List */}
          <div className="max-h-64 overflow-y-auto">
            {isLookingUp && (
              <div className="flex items-center justify-center gap-2 py-4 text-gray-500">
                <Loader className="animate-spin" size={16} />
                <span className="text-sm">Looking up Instagram username...</span>
              </div>
            )}
            
            {filteredOptions.length === 0 && !isLookingUp ? (
              <div className="px-4 py-6 text-center">
                <div className="text-gray-500 text-sm mb-2">No users found</div>
                <div className="text-xs text-gray-400">
                  Try searching for an Instagram username like @instagram or @cristiano
                </div>
              </div>
            ) : (
              filteredOptions.map((option, index) => renderUserOption(option, index))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
